import { normalize, schema } from 'normalizr';

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace Flipdish.MenuJson {
    interface Menu {
      MenuId: number;
      MenuVersionNumber: number;
      VersionGuid: string;
      MenuSections: MenuSection[];
      MenuSectionBehaviour: number;
      DisplaySectionLinks: boolean;
      ConcessionStores: ConcessionStore[];
    }

    interface MenuSection {
      MenuSectionId: number;
      Name: string;
      Description?: string | null;
      DisplayOrder: number;
      MenuItems: MenuItem[];
      PublicId: string;
      IsDeleted: boolean;
      IsAvailable: boolean;
      IsHiddenFromUsers: boolean;
      ImageName?: string | null;
      ImageUrl?: string | null;
      CellAspectRatio: number;
      CellLayoutType: number;
      MenuSectionAvailability: MenuSectionAvailability;
      ConcessionStoreId?: number | null;
      MenuSectionMetadata: any[];
    }

    interface MenuItem {
      ActualPrice: number;
      Alcohol: boolean;
      CellAspectRatio: number;
      CellLayoutType: number;
      ConcessionStoreId?: number;
      DailySpecialHours: any[];
      Description?: string | null;
      DisableVouchers: boolean;
      DisplayOrder: number;
      ExcludeFromVoucherDiscounting: boolean;
      ImageName?: string | null;
      ImageUrl?: string | null;
      IsAvailable: boolean;
      IsDeleted: boolean;
      MenuItemId: number;
      MenuItemMetadata: any[];
      MenuItemOptionSets: MenuItemOptionSet[];
      MenuSectionId: number;
      Name: string;
      Price: number;
      PriceCanIncrease: boolean;
      PublicId: string;
      SpicinessRating?: number | null;
      Tags: string[];
      TaxRate?: number | null;
      TaxRateId?: number | null;
      TaxValue: number;
    }

    interface MenuItemOptionSet {
      Name?: string | null;
      MenuItemOptionSetId: number;
      IsMasterOptionSet: boolean;
      DisplayOrder: number;
      MinSelectCount: number;
      MaxSelectCount: number;
      IsDeleted: boolean;
      PublicId: string;
      MenuItemOptionSetItems: MenuItemOptionSetItem[];
      ImageName?: string | null;
      ImageUrl?: string | null;
      CellAspectRatio: number;
      CellLayoutType: number;
      MinPrice: number;
      MenuItemId: number;
      MenuItemOptionSetMetadata: any[];
    }

    interface MenuItemOptionSetItem {
      MenuItemOptionSetItemId: number;
      Name: string;
      Price: number;
      TaxRateId?: number | null;
      TaxRate?: number | null;
      TaxValue?: number | null;
      IsAvailable: boolean;
      DisplayOrder: number;
      IsDeleted: boolean;
      Tags: string[];
      NextMenuItemOptionSetId?: number | null;
      PublicId: string;
      ImageName?: string | null;
      ImageUrl?: string | null;
      CellAspectRatio: number;
      CellLayoutType: number;
      OptionSetItemMetadata: any[];
    }

    interface MenuSectionAvailability {
      MenuSectionId: number;
      AvailableTimes?: BusinessHoursPeriod[] | null;
      AvailabilityMode: AvailabilityModeEnum;
    }

    interface BusinessHoursPeriod {
      BusinessHoursPeriodId: number;
      DayOfWeek: DayOfWeekEnum;
      StartTime: string;
      Period: string;
      StartTimeEarly?: string;
      PeriodEarly?: string;
    }

    enum DayOfWeekEnum {
      Sunday = 0,
      Monday = 1,
      Tuesday = 2,
      Wednesday = 3,
      Thursday = 4,
      Friday = 5,
      Saturday = 6,
    }

    enum AvailabilityModeEnum {
      DisplayAlways = 0,
      DisplayBasedOnTimes = 1,
      DisplayAlwaysStartCollapsed = 2,
      DisplayAlwaysStartCollapsedBasedOnTimes = 3,
    }

    interface ConcessionStore {
      ConcessionStoreId: number;
      Name: string;
      Description: string;
      ImageName: string;
      ImageUrl: string;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace FdMenuModule {
    // eslint-disable-next-line no-undef
    type Time = ReturnType<typeof parseTime>;
    // eslint-disable-next-line no-undef
    type TimePeriods = ReturnType<typeof parseBusinessHoursPeriod>;
    type DayOfWeek =
      | 'Sunday'
      | 'Monday'
      | 'Tuesday'
      | 'Wednesday'
      | 'Thursday'
      | 'Friday'
      | 'Saturday';

    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace Normalized {
      interface Menu extends Omit<Flipdish.MenuJson.Menu, 'MenuSections'> {
        MenuSections: number[];
      }

      interface Section
        extends Omit<
          Flipdish.MenuJson.MenuSection,
          'MenuItems' | 'MenuSectionAvailability'
        > {
        MenuId: number;
        MenuItems: number[];
        MenuSectionAvailability: number;
      }

      type SectionAvailabilityUnSanitized =
        Flipdish.MenuJson.MenuSectionAvailability;
      // eslint-disable-next-line no-undef
      type AvailableTimes = ReturnType<typeof parseAvailableTimes>;
      interface SectionAvailability
        extends Omit<
          Flipdish.MenuJson.MenuSectionAvailability,
          'AvailableTimes'
        > {
        AvailableTimes: AvailableTimes;
      }

      interface Item
        extends Omit<Flipdish.MenuJson.MenuItem, 'MenuItemOptionSets'> {
        MenuItemOptionSets: number[];
      }

      interface OptionSet
        extends Omit<
          Flipdish.MenuJson.MenuItemOptionSet,
          'MenuItemOptionSetItems'
        > {
        MenuItemOptionSetItems: number[];
      }

      interface OptionSetItem extends Flipdish.MenuJson.MenuItemOptionSetItem {
        MenuItemId: number;
        MenuItemOptionSetId: number;
      }
    }
  }
}

// #region parsing
function parseTime(time: string) {
  const [hours, minutes, seconds] = time.split(':').map(Number);
  return { hours, minutes, seconds };
}
function parseTimePeriod(start: string, period: string) {
  return [parseTime(start), parseTime(period)];
}
function parseBusinessHoursPeriod(oh: Flipdish.MenuJson.BusinessHoursPeriod) {
  const parsed = {} as {
    Start?: FdMenuModule.Time;
    Period?: FdMenuModule.Time;
    EarlyStart?: FdMenuModule.Time;
    EarlyPeriod?: FdMenuModule.Time;
  };

  if (oh.Period && oh.Period !== '00:00:00' && oh.StartTime) {
    const [start, period] = parseTimePeriod(oh.StartTime, oh.Period);

    parsed.Start = start;
    parsed.Period = period;
  }

  if (oh.PeriodEarly && oh.PeriodEarly !== '00:00:00' && oh.StartTimeEarly) {
    const [start, period] = parseTimePeriod(oh.StartTimeEarly, oh.PeriodEarly);

    parsed.EarlyStart = start;
    parsed.EarlyPeriod = period;
  }

  return parsed;
}
function parseDayOfWeek(dayOfWeek: Flipdish.MenuJson.DayOfWeekEnum) {
  switch (dayOfWeek) {
    case 1:
      return 'Monday';
    case 2:
      return 'Tuesday';
    case 3:
      return 'Wednesday';
    case 4:
      return 'Thursday';
    case 5:
      return 'Friday';
    case 6:
      return 'Saturday';
    case 0:
    default:
      return 'Sunday';
  }
}
function parseAvailableTimes(
  openingHours?: Flipdish.MenuJson.BusinessHoursPeriod[] | null,
) {
  let openingTimes = {} as {
    Sunday?: FdMenuModule.TimePeriods;
    Monday?: FdMenuModule.TimePeriods;
    Tuesday?: FdMenuModule.TimePeriods;
    Wednesday?: FdMenuModule.TimePeriods;
    Thursday?: FdMenuModule.TimePeriods;
    Friday?: FdMenuModule.TimePeriods;
    Saturday?: FdMenuModule.TimePeriods;
  };

  if (openingHours) {
    openingTimes = openingHours.reduce((acc, oh) => {
      const day = parseDayOfWeek(oh.DayOfWeek);
      const timePeriods = parseBusinessHoursPeriod(oh);
      acc[day] = timePeriods;
      return acc;
    }, openingTimes);
  }

  return openingTimes;
}
// #endregion

// #region schema

// #region MenuOptionSet
const optionSetItemProcessStrategy = (
  value: Flipdish.MenuJson.MenuItemOptionSetItem,
  parent: Flipdish.MenuJson.MenuItemOptionSet,
) => ({
  ...value,
  MenuItemOptionSetId: parent.MenuItemOptionSetId,
  MenuItemId: parent.MenuItemId,
  // fix types: null | undefined | number => undefined | number
  TaxRateId: value.TaxRateId || undefined,
  TaxValue: value.TaxValue || 0,
  ImageUrl: value.ImageUrl || undefined,
});
const optionSetItemSchema = new schema.Entity(
  'optionSetItems',
  {},
  {
    idAttribute: 'MenuItemOptionSetItemId',
    processStrategy: optionSetItemProcessStrategy,
  },
);

const optionSetProcessStrategy = (
  value: Flipdish.MenuJson.MenuItemOptionSet,
  _parent: Flipdish.MenuJson.MenuItem,
) => ({
  ...value,
  // MenuItemOptionSetId: parent.MenuItemOptionSetId,
  // fix types: null | undefined | number => undefined | number
  Name: value.Name || undefined,
  ImageName: value.ImageName || undefined,
  ImageUrl: value.ImageUrl || undefined,
});
const optionSetSchema = new schema.Entity(
  'optionSets',
  {
    MenuItemOptionSetItems: [optionSetItemSchema],
  },
  {
    idAttribute: 'MenuItemOptionSetId',
    processStrategy: optionSetProcessStrategy,
  },
);
// #endregion

// #region MenuItem
const menuItemProcessStrategy = (value: Flipdish.MenuJson.MenuItem) => ({
  ...value,
  // fix types: null | undefined | number => undefined | number
  TaxRateId: value.TaxRateId || undefined,
  TaxValue: value.TaxValue || 0,
});
const menuItemSchema = new schema.Entity(
  'items',
  {
    MenuItemOptionSets: [optionSetSchema],
  },
  {
    idAttribute: 'MenuItemId',
    processStrategy: menuItemProcessStrategy,
  },
);
// #endregion

// #region MenuSection
const menuSectionAvailableTimesProcessStrategy = (
  value: Flipdish.MenuJson.MenuSectionAvailability,
) => ({
  ...value,
});
const menuSectionAvailableTimesSchema = new schema.Entity(
  'sectionAvailableTimes',
  {},
  {
    idAttribute: 'MenuSectionId',
    processStrategy: menuSectionAvailableTimesProcessStrategy,
  },
);

const menuSectionProcessStrategy = (
  value: Flipdish.MenuJson.MenuSection,
  parent: Flipdish.MenuJson.Menu,
) => ({
  ...value,
  MenuId: parent.MenuId,
  // fix types: null | undefined | number => undefined | number
  Description: value.Description || undefined,
  ImageUrl: value.ImageUrl || undefined,
});
const menuSectionSchema = new schema.Entity(
  'sections',
  {
    MenuItems: [menuItemSchema],
    MenuSectionAvailability: menuSectionAvailableTimesSchema,
  },
  {
    idAttribute: 'MenuSectionId',
    processStrategy: menuSectionProcessStrategy,
  },
);
// #endregion

// #region Menu
const menuSchema = new schema.Entity(
  'menus',
  {
    MenuSections: [menuSectionSchema],
  },
  {
    idAttribute: 'MenuId',
  },
);
// #endregion

// #endregion

// #region normalization
type OptionSetDictionary = {
  [MenuItemOptionSetId: number]: FdMenuModule.Normalized.OptionSet;
};
type OptionSetItemDictionary = {
  [MenuItemOptionSetItemId: number]: FdMenuModule.Normalized.OptionSetItem;
};
type ItemDictionary = {
  [MenuItemId: number]: FdMenuModule.Normalized.Item;
};
type SectionAvailableTimesDictionary = {
  [
    MenuSectionId: number
  ]: FdMenuModule.Normalized.SectionAvailabilityUnSanitized;
};
type SectionDictionary = {
  [MenuSectionId: number]: FdMenuModule.Normalized.Section;
};
type MenuDictionary = {
  [MenuId: number]: FdMenuModule.Normalized.Menu;
};

export const normalizeOptionSet = (
  payload: Flipdish.MenuJson.MenuItemOptionSet,
) => {
  const { entities, result } = normalize<
    Flipdish.MenuJson.MenuItemOptionSet,
    {
      optionSets?: OptionSetDictionary;
      optionSetItems?: OptionSetItemDictionary;
    }
  >(payload, optionSetSchema);

  const { optionSets = {}, optionSetItems = {} } = entities;
  return {
    optionSetId: (result as number) || 0,
    optionSets,
    optionSetItems,
  };
};
export const normalizeMenuItem = (payload: Flipdish.MenuJson.MenuItem) => {
  const { entities, result } = normalize<
    Flipdish.MenuJson.MenuItem,
    {
      items?: ItemDictionary;
      optionSets?: OptionSetDictionary;
      optionSetItems?: OptionSetItemDictionary;
    }
  >(payload, menuItemSchema);

  const { items = {}, optionSets = {}, optionSetItems = {} } = entities;
  return {
    itemId: (result as number) || 0,
    items,
    optionSets,
    optionSetItems,
  };
};
export const normalizeMenuSection = (
  payload: Flipdish.MenuJson.MenuSection,
) => {
  const { entities, result } = normalize<
    Flipdish.MenuJson.MenuSection,
    {
      sections?: SectionDictionary;
      sectionAvailableTimes?: SectionAvailableTimesDictionary;
      items?: ItemDictionary;
      optionSets?: OptionSetDictionary;
      optionSetItems?: OptionSetItemDictionary;
    }
  >(payload, menuSectionSchema);

  const {
    sections = {},
    sectionAvailableTimes = {},
    items = {},
    optionSets = {},
    optionSetItems = {},
  } = entities;
  return {
    sectionId: (result as number) || 0,
    sections,
    sectionAvailableTimes,
    items,
    optionSets,
    optionSetItems,
  };
};
export const normalizeMenu = (payload: Flipdish.MenuJson.Menu) => {
  const { entities, result } = normalize<
    Flipdish.MenuJson.Menu,
    {
      menus: MenuDictionary;
      sections?: SectionDictionary;
      sectionAvailableTimes?: SectionAvailableTimesDictionary;
      items?: ItemDictionary;
      optionSets?: OptionSetDictionary;
      optionSetItems?: OptionSetItemDictionary;
    }
  >(payload, menuSchema);

  const {
    menus = {},
    sections = {},
    sectionAvailableTimes = {},
    items = {},
    optionSets = {},
    optionSetItems = {},
  } = entities;
  return {
    menuId: (result as number) || 0,
    menus,
    sections,
    sectionAvailableTimes,
    items,
    optionSets,
    optionSetItems,
  };
};
// #endregion

export const sanitizeMenuSectionAvailableTimes = (sectionAvailableTimes: {
  [MenuSectionId: number]: Flipdish.MenuJson.MenuSectionAvailability;
}) => {
  const sanitized = {} as {
    [MenuSectionId: number]: FdMenuModule.Normalized.SectionAvailability;
  };

  Object.values(sectionAvailableTimes).forEach((sat) => {
    if (sat.AvailableTimes) {
      sanitized[sat.MenuSectionId] = {
        ...sat,
        AvailableTimes: parseAvailableTimes(sat.AvailableTimes),
      };
    }
  });

  return sanitized;
};

/* resolve modifiers
 * option.NextMenuItemOptionSetId
 * null    -> next optionSet (if any)
 * -1      -> it's the last item
 * integer -> skip to the specified option set.
 */
export const resolveNextMenuItemOptionSetId = (
  optionSetItem: FdMenuModule.Normalized.OptionSetItem,
  optionSets: number[],
) => {
  if (optionSetItem.NextMenuItemOptionSetId === null) {
    const idx = optionSets.findIndex(
      (osId) => osId === optionSetItem.MenuItemOptionSetId,
    );
    if (idx + 1 <= optionSets.length) {
      return {
        ...optionSetItem,
        NextMenuItemOptionSetId: optionSets[idx + 1],
      };
    }
    return { ...optionSetItem, NextMenuItemOptionSetId: -1 };
  }
  return optionSetItem;
};

// #region sorting
export function sortByDisplayOrder<
  T extends {
    DisplayOrder: number;
  },
>(a: T, b: T) {
  return a.DisplayOrder - b.DisplayOrder;
}
// #endregion
